import _ from 'lodash';
import { sqExploreFolderModalStore, sqHomeScreenStore } from '@/core/core.stores';
import { tabFolders } from '@/homescreen/homescreen.constants';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { isAdmin } from '@/services/authorization.service';
import { isEveryoneGroupEnabled, isExternalOrganizationTabEnabled } from '@/services/systemConfiguration.utilities';
import { formatNestedFolderWithAncestors, getTabFolder, getTabFolderName } from '@/utilities/homescreen.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import { sqFoldersApi } from '@seeqdev/api/endpoint/FoldersApi';
import type { Folder, InitialFolderParams, SearchParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type {
  GetFoldersFilterEnum as FilterEnum,
  GetFoldersRootEnum as RootEnum,
} from '@seeqdev/api/endpoint/FoldersApi';

/**
 * Sets the ID of the folder where the user started
 */
export function setInitialFolderId(initialFolderId?: string) {
  sqExploreFolderModalStore.actions.setInitialFolderId(initialFolderId);
}

/** Sets the workbook ID */
export function setWorkbookId(workbookId?: string) {
  sqExploreFolderModalStore.actions.setWorkbookId(workbookId);
}

/**
 * Sets the sort field and toggles the direction if the field was already set
 * to the specified one.
 *
 * @param sortProperty - One of WORKBOOKS_SORT_FIELDS
 * @param ascending - True for asc, false for desc
 * @param forceSortDirection - True if the given direction must not be flipped when
 *   sorting on the currently sorted property
 */
export function setSort(sortProperty: string, ascending: boolean, forceSortDirection = false) {
  const sortAscending =
    !forceSortDirection && sortProperty === sqExploreFolderModalStore.getState().sortProperty ? !ascending : ascending;
  sqExploreFolderModalStore.actions.setSort({ sortProperty, sortAscending });
}

/** Clears the data for the currently loaded seeq content image. */
export function clear() {
  sqExploreFolderModalStore.actions.resetToSessionPreferences();
}

export function setTableFilter(tableFilter: SearchItemLocation) {
  sqExploreFolderModalStore.actions.setTableFilter(tableFilter);
}

export function setSearchParams(field: keyof SearchParams | string, value: unknown) {
  sqExploreFolderModalStore.actions.setSearchParam(field as keyof SearchParams, value);
}

export function setPageSizeForTable({ size }: { size: number }) {
  sqExploreFolderModalStore.actions.setPageSize(size);
}

export function setPageNumberAndGo(page: number, loadTable: (...args: any[]) => any) {
  sqExploreFolderModalStore.actions.setPageNumber(page);
  loadTable();
}

export function setFolder(folder?: Folder) {
  sqExploreFolderModalStore.actions.setFolder(folder);
}

export function setShowResults(showResults: boolean) {
  sqExploreFolderModalStore.actions.setShowResults(showResults);
}

export function setTableRoot(root?: string) {
  sqExploreFolderModalStore.actions.setTableRoot(root as RootEnum | undefined);
}

export function loadItems() {
  const state = sqExploreFolderModalStore.getState();
  if (_.isUndefined(state.initialFolderId)) {
    return;
  }

  const limit = state.pageSize;
  const offset = (state.pageNumber - 1) * limit;
  const sortDirection = state.sortAscending ? 'asc' : 'desc';
  const sortOrder = `${state.sortProperty} ${sortDirection}`;
  const root = state.tableRoot;
  const filter = state.searchParams.filter ?? (root as unknown as FilterEnum);
  const params = {
    ...state.searchParams,
    limit,
    offset,
    sortOrder,
    root,
    filter:
      state.initialFolderId === null && !state.searchParams.textSearch
        ? (SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC as unknown as FilterEnum)
        : filter,
  };

  const cancellationGroup = 'exploreFolderModalLoadItems';
  sqExploreFolderModalStore.actions.setLoading(true);
  cancelGroup(cancellationGroup);
  return sqFoldersApi
    .getFolders(params, { cancellationGroup })
    .then((response) => {
      sqExploreFolderModalStore.actions.setItems(response.data?.content ?? []);
      sqExploreFolderModalStore.actions.setItemTotalForTable(response.data?.totalResults ?? 0);
    })
    .finally(() => sqExploreFolderModalStore.actions.setLoading(false));
}

export function getRootAndUpdateFilter(folderId: string) {
  if (folderId) {
    return sqFoldersApi.getAncestors({ folderId }).then((folderTree) => {
      const itemFolderRoot = folderTree.data.id;
      if (
        _.includes(
          [SEARCH_ITEM_LOCATIONS.CORPORATE, SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC, SEARCH_ITEM_LOCATIONS.MY_FOLDER],
          itemFolderRoot,
        )
      ) {
        setTableFilter(itemFolderRoot as SearchItemLocation);
        setSearchParams('filter', itemFolderRoot);
      }
    });
  } else {
    return Promise.resolve({});
  }
}

export function getParentFolderId(currentTab: string, item: any) {
  sqFoldersApi.getAncestors({ folderId: item?.id }).then((result) => {
    const locationValue = item.name === 'Corporate folder' ? SEARCH_ITEM_LOCATIONS.CORPORATE : result.data.id;
    getInitialFolder({
      currentTab,
      item,
      parentFolderId: result.data.id,
      locationValue,
      currentFolderId: item?.id,
    });
  });
}

/**
 * @param forceCorporate - checks if the function is being called within the corporate drive message,
 * which will default the folder to being the corporate drive
 */
export function getInitialFolder({
  currentTab,
  item,
  forceCorporate = false,
  insideAdvancedSearch = false,
  isCurrentFolderNested = false,
  breadcrumbsPresent = false,
  breadcrumbClicked = false,
  locationChangedInAdvanced = false,
  currentFolderId = '',
  locationValue = '',
  parentFolderId = '',
}: InitialFolderParams) {
  let tabFolderPromise;
  let tableFilter = currentTab;
  const tabFolder = _.includes(
    tabFolders({
      isAdmin: isAdmin(),
      includeCorporate: isEveryoneGroupEnabled(),
      includeExternallyShared: isExternalOrganizationTabEnabled(),
    }),
    currentTab,
  )
    ? currentTab
    : undefined;

  getTabFolder(tabFolder).then((folder: Folder) => {
    let updatedFolder = _.cloneDeep(folder);
    if (
      (item !== undefined && !forceCorporate && insideAdvancedSearch) ||
      (item && item?.parentFolderId && !equalsIgnoreCase(item.parentFolderId, updatedFolder?.id) && !forceCorporate)
    ) {
      tabFolderPromise = getTabFolderName(item.ancestors[0]?.id);
      tabFolderPromise.then((tabName) => {
        tableFilter = item.ancestors[0]?.id ? (tabName ?? '') : tableFilter;
        updatedFolder = insideAdvancedSearch
          ? item
          : _.assign({}, _.last(item.ancestors), {
              ancestors: _.slice(item.ancestors, 0, item.ancestors.length - 1),
            });
      });
    } else {
      tabFolderPromise = Promise.resolve();
    }

    // If the user is inside a nested folder and they are opening the specific folder modal within the home
    // screen advanced search, then the initial folder needs to be set to the folder of the current folder id.
    // When a user changes the location of the search or clicks on a breadcrumb, the modal should no longer show
    // the contents of the current folder id, so this condition will be skipped over.
    if (
      (isCurrentFolderNested &&
        insideAdvancedSearch &&
        breadcrumbsPresent &&
        !breadcrumbClicked &&
        !locationChangedInAdvanced &&
        currentFolderId &&
        !item) ||
      parentFolderId
    ) {
      return tabFolderPromise.then(() => {
        formatNestedFolderWithAncestors(
          currentFolderId ?? sqHomeScreenStore.currentFolderId,
          locationValue,
          false,
        ).then((response) => {
          let filter: string = parentFolderId;
          if (response?.name === 'Corporate folder') {
            filter = SEARCH_ITEM_LOCATIONS.CORPORATE;
          } else if (response?.name === 'Users folder') {
            filter = SEARCH_ITEM_LOCATIONS.USERS;
          }

          setFolder(response);
          setTableFilter(
            (filter as SearchItemLocation) ?? SEARCH_ITEM_LOCATIONS[tableFilter as keyof typeof SEARCH_ITEM_LOCATIONS],
          );
          setSearchParams('folderId', response?.id);
          setInitialFolderId(response?.id);
        });
      });
    } else {
      const advancedSearchId = item ? item?.id : updatedFolder?.id;
      const folderId = item && !forceCorporate && !insideAdvancedSearch ? item?.parentFolderId : advancedSearchId;
      return tabFolderPromise.then(() => {
        setFolder(updatedFolder);
        setTableFilter(SEARCH_ITEM_LOCATIONS[tableFilter as keyof typeof SEARCH_ITEM_LOCATIONS]);
        setSearchParams('folderId', folderId);
        setInitialFolderId(folderId);
      });
    }
  });
}
